/**
 * SyncProgressModal Component
 * Shows sync progress, results, and errors when syncing local documents to cloud.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, CheckCircle, AlertCircle, Cloud } from 'lucide-react';
import type { SyncResult } from '@/hooks/useDocumentStorage';

interface SyncProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  isSyncing: boolean;
  result: SyncResult | null;
  error: string | null;
}

export const SyncProgressModal: React.FC<SyncProgressModalProps> = ({
  isOpen,
  onClose,
  isSyncing,
  result,
  error,
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={!isSyncing ? onClose : undefined}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        {/* Close button (only when not syncing) */}
        {!isSyncing && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        {/* Syncing State */}
        {isSyncing && (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {t('sync.syncingToCloud')}
            </h2>
            <p className="text-slate-600">
              {t('sync.uploadingSecurely')}
            </p>
            <p className="text-sm text-slate-500 mt-2">
              {t('sync.dontCloseWindow')}
            </p>
          </div>
        )}

        {/* Success/Partial Success State */}
        {!isSyncing && result && (
          <div className="text-center py-4">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
              result.success ? 'bg-emerald-100' : 'bg-amber-100'
            }`}>
              {result.success ? (
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              ) : (
                <AlertCircle className="w-8 h-8 text-amber-600" />
              )}
            </div>

            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {result.success ? t('sync.syncComplete') : t('sync.syncPartial')}
            </h2>

            <div className="text-slate-600 mb-4 space-y-1">
              {result.syncedCount > 0 && (
                <p className="flex items-center justify-center gap-2">
                  <Cloud className="w-4 h-4 text-emerald-600" />
                  {t('sync.documentsSynced', { count: result.syncedCount })}
                </p>
              )}
              {result.failedCount > 0 && (
                <p className="text-amber-600">
                  {t('sync.documentsFailed', { count: result.failedCount })}
                </p>
              )}
              {result.syncedCount === 0 && result.failedCount === 0 && (
                <p>{t('sync.noDocumentsToSync')}</p>
              )}
            </div>

            {/* Error details */}
            {result.errors.length > 0 && (
              <div className="text-left bg-amber-50 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-amber-800 mb-1">{t('sync.issues')}</p>
                <ul className="text-xs text-amber-700 space-y-1">
                  {result.errors.slice(0, 3).map((err, i) => (
                    <li key={i} className="truncate">
                      {err.error}
                    </li>
                  ))}
                  {result.errors.length > 3 && (
                    <li className="text-amber-600">
                      {t('sync.andMore', { count: result.errors.length - 3 })}
                    </li>
                  )}
                </ul>
              </div>
            )}

            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors"
            >
              {t('sync.done')}
            </button>
          </div>
        )}

        {/* Error State (when sync completely failed) */}
        {!isSyncing && !result && error && (
          <div className="text-center py-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">
              {t('sync.syncFailed')}
            </h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors"
            >
              {t('sync.close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SyncProgressModal;
